import { ref, computed, inject, watch } from 'vue';

// COMPOSABLES
import { toast } from '@flash-global66/g-toast';
import { useLoader } from '@flash-global66/g-loader';
import { usePinFlow } from '../../composables/usePinFlow';

// CONFIG
import GSteppersProvider, {
  initialGSteppersProvider,
} from '@/components/GStepper/config/gSteppersProvider';

// HELPERS
import { logOut } from '@/helpers/auth';
import { verifyPinSecurity } from '../../utils/verifyPinSecurity';
import { ErrorsPin } from '@/shared/common/errors';

// API
import { pinAuthService } from '../../services/pinAuth.service';

// TYPES
import type { PinEmits } from '../../types/pin.type';
import { PinCreateStep } from '../../types/pin.type';

export const usePinCreateForm = (emits: PinEmits) => {
  type PinInputName = 'PRIMARY' | 'SECONDARY';

  const pinFlow = usePinFlow();
  const { showGLoader } = useLoader();

  const primaryPin = ref('');
  const secondaryPin = ref('');
  const primaryErrorMessage = ref('');
  const secondaryErrorMessage = ref('');
  const isPrimaryComplete = ref(false);
  const isSecondaryComplete = ref(false);

  const { gStepperMethods } = inject(
    GSteppersProvider,
    initialGSteppersProvider
  );

  function onCompletePrimary(isComplete: boolean) {
    isPrimaryComplete.value = isComplete;
    primaryErrorMessage.value = isComplete
      ? verifyPinSecurity({ pin: primaryPin.value })
      : '';
  }

  function onCompleteSecondary(isComplete: boolean) {
    isSecondaryComplete.value = isComplete;
    secondaryErrorMessage.value = isComplete
      ? verifyPinSecurity({
          pin: secondaryPin.value,
        })
      : '';
  }

  watch(
    [isPrimaryComplete, isSecondaryComplete],
    ([isPrimaryComplete, isSecondaryComplete]) => {
      if (isPrimaryComplete && isSecondaryComplete) {
        return primaryPin.value !== secondaryPin.value
          ? (secondaryErrorMessage.value = 'El Pin no coincide')
          : null;
      }
    }
  );

  const hasErrors = computed(() =>
    Boolean(secondaryErrorMessage.value || primaryErrorMessage.value)
  );

  const isDisableOnContinue = computed(() => {
    if (hasErrors.value) return true;
    if (isPrimaryComplete.value && isSecondaryComplete.value) return false;

    return true;
  });

  function onChangeCode(pin: string, inputName: PinInputName) {
    inputName === 'PRIMARY'
      ? (primaryPin.value = pin)
      : (secondaryPin.value = pin);
  }

  async function onCloseSession() {
    await logOut();
    emits('close');
    pinFlow.hidePin();
  }

  async function onContinue() {
    showGLoader(true);

    const result = await pinAuthService.createPin(secondaryPin.value);

    result.fold(
      (error) => {
        const errorMessage = ErrorsPin[error.reason] || 'Error al crear PIN';
        toast.error(errorMessage);
      },
      () => {
        gStepperMethods.navigateTo(PinCreateStep.SUCCESS);
      }
    );

    showGLoader(false);
  }

  function onKeyPressToValidate() {
    if (!isDisableOnContinue.value) {
      onContinue();
    }
  }

  return {
    primaryErrorMessage,
    secondaryErrorMessage,
    isDisableOnContinue,
    onChangeCode,
    onCompletePrimary,
    onCompleteSecondary,
    onKeyPressToValidate,
    onContinue,
    onCloseSession,
  };
};
